/* ------------------------------------------------------------------
   ÉKO '77 — the side basket drawer.

   THE CONTRACT (already honoured by two shipped components — do not
   rebuild either of them):
     · sections/x-cartbar.liquid (the sticky mobile bar) and
       snippets/x-head-tools.liquid (the round basket tool in the
       header) each fire a CANCELABLE `eko:basket:open` on document
       before doing anything else. This file cancels it and slides in.
       With this file absent they fall back to the theme drawer and
       then to the cart page, exactly as before.
     · This file answers with `eko:basket:opened` / `eko:basket:closed`.
       The cartbar reproduces the reference rule
       `show = items > 0 && !basket.is-open` from those two events, so
       the bar tucks away while the drawer is open. Renaming either
       event silently breaks the bar.
     · After it mutates the cart it publishes the theme pub/sub
       `cart-update` AND dispatches `eko:cart:updated`, the same pair
       assets/x-quick-buy.js publishes — so anything listening to one of
       them stays in sync.

   NO MONEY, NO PLURALS, NO TOTALS ARE FORMATTED HERE. Every mutation
   posts to /cart/change.js asking for this section back through the
   Cart AJAX `sections` parameter, and the returned Liquid replaces the
   body and the foot. That keeps the shop's money_format, the theme's
   translated strings and the `serves` variant metafield (which is NOT
   in /cart.js) — and makes it impossible for the drawer to drift from
   the real cart. The only number this file touches is the integer in
   the stepper, painted optimistically so a fast tap still feels
   instant, then overwritten by the server's answer.
   ------------------------------------------------------------------ */
(function () {
  'use strict';

  if (customElements.get('x-basket')) return;

  var CLOSE_MS = 360; /* the reference waits 360ms before hiding the scrim */
  var COALESCE_MS = 250; /* rapid taps on one row become one request */

  function root() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  customElements.define(
    'x-basket',
    class XBasket extends HTMLElement {
      connectedCallback() {
        this.sectionId = this.dataset.sectionId;
        this.cartUrl = this.dataset.cartUrl || root() + 'cart';
        this.designMode = this.dataset.designMode === 'true';

        this.drawer = this.querySelector('[data-x-basket]');
        this.scrim = this.querySelector('[data-x-basket-scrim]');
        this.opener = null;
        this.pending = {}; /* line -> { quantity, timer } */
        this.busy = 0;

        this.onOpenRequest = this.onOpenRequest.bind(this);
        this.onKeydown = this.onKeydown.bind(this);
        this.onClick = this.onClick.bind(this);
        this.onCartUpdated = this.onCartUpdated.bind(this);
        this.onNoteInput = this.onNoteInput.bind(this);

        document.addEventListener('eko:basket:open', this.onOpenRequest);
        document.addEventListener('keydown', this.onKeydown);

        /* Remember which button was actually pressed, in the CAPTURE phase
           so it lands before the opener dispatches `eko:basket:open`.
           Needed because the two openers announce themselves differently:
           x-cartbar fires on ITSELF (event.target is the component, and the
           button is findable inside it) but x-site-head fires on DOCUMENT,
           where event.target is the document and carries no element at all.
           Falling back to document.activeElement is not enough — Safari
           does not focus a <button> on click, so focus would be dropped to
           <body> on close. x-nav-panel.js hit this same thing. */
        this.onTriggerCapture = this.onTriggerCapture.bind(this);
        document.addEventListener('click', this.onTriggerCapture, true);
        document.addEventListener('eko:cart:updated', this.onCartUpdated);
        this.addEventListener('click', this.onClick);
        this.addEventListener('input', this.onNoteInput);

        /* Dawn's own pub/sub — anything in the theme that adds to cart */
        if (typeof window.subscribe === 'function' && window.PUB_SUB_EVENTS) {
          this.unsubscribe = window.subscribe(window.PUB_SUB_EVENTS.cartUpdate, function () {
            this.refresh();
          }.bind(this));
        }

        /* the theme editor renders the section with the drawer closed,
           which would look like nothing happened when it is selected */
        if (this.designMode) {
          document.addEventListener('shopify:section:select', function (event) {
            if (event.detail && event.target && event.target.contains(this)) this.open();
          }.bind(this));
          document.addEventListener('shopify:section:deselect', function () {
            this.close();
          }.bind(this));
        }
      }

      disconnectedCallback() {
        document.removeEventListener('eko:basket:open', this.onOpenRequest);
        document.removeEventListener('keydown', this.onKeydown);
        document.removeEventListener('click', this.onTriggerCapture, true);
        document.removeEventListener('eko:cart:updated', this.onCartUpdated);
        if (typeof this.unsubscribe === 'function') this.unsubscribe();
        this.unlock();
      }

      get isOpen() {
        return !!this.drawer && this.drawer.classList.contains('is-open');
      }

      /* ---------------- open / close ---------------- */

      onOpenRequest(event) {
        /* claim it: the bar and the header both fall back to the cart
           page if nobody cancels */
        event.preventDefault();
        this.opener = this.resolveOpener(event);
        this.open();
      }

      /* Where focus goes on close. The bar fires the event on ITSELF and
         it bubbles to document, so event.target is usually the component
         rather than the button — and a click does not reliably focus a
         <button> (Safari never does). Prefer the real button inside the
         opener, then the active element. */
      onTriggerCapture(event) {
        var trigger =
          event.target.closest &&
          event.target.closest('[data-x-head-basket], [data-x-cartbar-open], [data-x-basket-open]');
        if (trigger) this.lastTrigger = trigger;
      }

      resolveOpener(event) {
        var target = event && event.target;
        if (target && target.nodeType === 1) {
          var button = target.matches && target.matches('button') ? target : target.querySelector && target.querySelector('button');
          if (button && typeof button.focus === 'function') return button;
          if (typeof target.focus === 'function' && target !== document.body) return target;
        }
        /* the button we saw being pressed, whether or not it took focus */
        if (this.lastTrigger && document.contains(this.lastTrigger)) return this.lastTrigger;
        var active = document.activeElement;
        if (active && active !== document.body && typeof active.focus === 'function' && !this.contains(active)) {
          return active;
        }
        return null;
      }

      open() {
        if (!this.drawer || this.isOpen) return;

        if (this.scrim) {
          this.scrim.hidden = false;
          /* one frame between display and opacity, or the fade is skipped */
          requestAnimationFrame(function () {
            if (this.scrim) this.scrim.classList.add('is-on');
          }.bind(this));
        }

        this.drawer.classList.add('is-open');
        this.drawer.setAttribute('aria-hidden', 'false');
        this.lock();

        clearTimeout(this.closeTimer);

        var close = this.querySelector('[data-x-basket-close]');
        if (close) close.focus();

        /* the cartbar tucks itself away on this */
        document.dispatchEvent(new CustomEvent('eko:basket:opened', { bubbles: true }));

        /* it may have been opened from a page where the cart changed in
           another tab */
        this.refresh();
      }

      close() {
        if (!this.drawer || !this.isOpen) return;

        this.drawer.classList.remove('is-open');
        this.drawer.setAttribute('aria-hidden', 'true');
        if (this.scrim) this.scrim.classList.remove('is-on');
        this.unlock();

        clearTimeout(this.closeTimer);
        this.closeTimer = setTimeout(
          function () {
            if (this.scrim && !this.isOpen) this.scrim.hidden = true;
          }.bind(this),
          CLOSE_MS
        );

        /* never leave focus on something inside a closed dialog */
        if (this.opener && document.contains(this.opener)) {
          this.opener.focus();
        } else if (this.contains(document.activeElement)) {
          document.activeElement.blur();
        }
        this.opener = null;

        document.dispatchEvent(new CustomEvent('eko:basket:closed', { bubbles: true }));
      }

      lock() {
        document.body.style.overflow = 'hidden';
      }

      unlock() {
        document.body.style.overflow = '';
      }

      /* ---------------- input ---------------- */

      onClick(event) {
        if (event.target.closest('[data-x-basket-close]') || event.target.closest('[data-x-basket-scrim]')) {
          event.preventDefault();
          this.close();
          return;
        }

        var step = event.target.closest('[data-x-basket-step]');
        if (step) {
          event.preventDefault();
          this.step(step);
        }
      }

      onKeydown(event) {
        if (!this.isOpen) return;
        if (event.key === 'Escape') {
          event.preventDefault();
          this.close();
          return;
        }
        if (event.key === 'Tab') this.trap(event);
      }

      trap(event) {
        var focusable = this.drawer.querySelectorAll(
          'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])'
        );
        if (!focusable.length) return;
        var first = focusable[0];
        var last = focusable[focusable.length - 1];

        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        } else if (!this.drawer.contains(document.activeElement)) {
          event.preventDefault();
          first.focus();
        }
      }

      /* ---------------- quantity ----------------
         The reference removes the row when "−" takes it below 1
         (`if (lines[i].qty <= 0) lines.splice(i, 1)`), so quantity 0 IS
         the delete action — there is no separate bin icon to build. */
      step(button) {
        var line = button.dataset.line;
        var delta = parseInt(button.dataset.xBasketStep, 10) || 0;
        var row = button.closest('[data-x-line]');
        var counter = row && row.querySelector('[data-x-line-count]');
        if (!line || !counter) return;

        var pendingLine = this.pending[line];
        var current = pendingLine ? pendingLine.quantity : parseInt(counter.textContent, 10) || 0;
        var next = Math.max(0, current + delta);

        /* paint immediately so a fast tap accumulates locally; the
           server's re-render overwrites this a moment later */
        counter.textContent = next;
        if (next === 0) row.style.opacity = '0.45';

        if (pendingLine) clearTimeout(pendingLine.timer);
        this.pending[line] = {
          quantity: next,
          timer: setTimeout(
            function () {
              var target = this.pending[line];
              delete this.pending[line];
              if (target) this.change(line, target.quantity);
            }.bind(this),
            COALESCE_MS
          ),
        };
      }

      async change(line, quantity) {
        this.setBusy(1);
        try {
          var response = await fetch(root() + 'cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({
              line: Number(line),
              quantity: quantity,
              sections: this.sectionId,
              sections_url: window.location.pathname,
            }),
          });
          if (!response.ok) throw new Error(response.status);
          var cart = await response.json();
          this.paint(cart);
          this.announce(cart);
        } catch (error) {
          /* the optimistic number is now a lie — take the truth back */
          this.refresh();
        } finally {
          this.setBusy(-1);
        }
      }

      /* the order note is the native Shopify cart note; it also rides the
         checkout form, so this is only about keeping it if the customer
         leaves without checking out */
      onNoteInput(event) {
        var field = event.target.closest('[data-x-basket-order-note]');
        if (!field) return;
        clearTimeout(this.noteTimer);
        this.noteTimer = setTimeout(function () {
          fetch(root() + 'cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ note: field.value }),
          }).catch(function () {});
        }, 500);
      }

      /* ---------------- rendering ---------------- */

      onCartUpdated(event) {
        if (event && event.detail && event.detail.source === 'x-basket') return;
        this.refresh();
      }

      /* pull this section fresh — used when the cart changed elsewhere
         (quick buy, another tab, the theme editor) */
      async refresh() {
        if (!this.sectionId) return;
        if (this.refreshAbort) this.refreshAbort.abort();
        this.refreshAbort = new AbortController();

        try {
          var url = window.location.pathname + '?sections=' + encodeURIComponent(this.sectionId);
          var response = await fetch(url, { signal: this.refreshAbort.signal });
          if (!response.ok) throw new Error(response.status);
          var data = await response.json();
          if (data[this.sectionId]) this.swap(data[this.sectionId]);
        } catch (error) {
          /* leave the last good render up rather than blanking the
             drawer on a flaky connection */
        }
      }

      paint(cart) {
        if (cart && cart.sections && cart.sections[this.sectionId]) {
          this.swap(cart.sections[this.sectionId]);
        }
        if (cart && typeof cart.item_count === 'number') {
          this.dataset.itemCount = cart.item_count;
        }

        /* tell the rest of the theme, the same pair x-quick-buy publishes */
        document.dispatchEvent(new CustomEvent('eko:cart:updated', { detail: { source: 'x-basket' } }));
        if (typeof window.publish === 'function' && window.PUB_SUB_EVENTS) {
          window.publish(window.PUB_SUB_EVENTS.cartUpdate, { source: 'x-basket' });
        }
      }

      /* Only the body and the foot are replaced. Swapping the whole
         drawer would restart its slide-in transition and drop focus
         mid-interaction. */
      swap(html) {
        var fresh = new DOMParser().parseFromString(html, 'text/html');
        var keepFocus = document.activeElement;
        var focusLine = null;
        var focusStep = null;
        if (keepFocus && this.contains(keepFocus) && keepFocus.dataset && keepFocus.dataset.line) {
          focusLine = keepFocus.dataset.line;
          focusStep = keepFocus.dataset.xBasketStep;
        }

        ['[data-x-basket-body]', '[data-x-basket-foot]'].forEach(
          function (selector) {
            var next = fresh.querySelector(selector);
            var current = this.querySelector(selector);
            if (!next || !current) return;

            /* an order note being typed into must not be clobbered by a
               refresh triggered from somewhere else */
            var note = current.querySelector('[data-x-basket-order-note]');
            var freshNote = next.querySelector('[data-x-basket-order-note]');
            if (note && freshNote && document.activeElement === note) freshNote.value = note.value;

            current.replaceWith(next);
          }.bind(this)
        );

        /* the row the customer was clicking has just been replaced —
           put focus back on the same button so the keyboard does not
           jump to the top of the page */
        if (focusLine) {
          var again = this.querySelector(
            '[data-x-basket-step="' + focusStep + '"][data-line="' + focusLine + '"]'
          );
          if (again) again.focus();
        }
      }

      /* a live-region sentence for screen readers: the visible drawer
         updates silently otherwise */
      announce(cart) {
        if (!cart || typeof cart.item_count !== 'number') return;
        var region = document.getElementById('cart-live-region-text');
        if (region) region.setAttribute('aria-hidden', 'false');
      }

      setBusy(delta) {
        this.busy = Math.max(0, this.busy + delta);
        if (this.drawer) this.drawer.setAttribute('aria-busy', this.busy > 0 ? 'true' : 'false');
      }
    }
  );
})();
